// External modules
import { encodeAbiParameters, encodePacked, erc20Abi, type PublicClient, type WalletClient } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import Permit2ABI from './abi/Permit2.json';
import universalRouterABI from './abi/universalRouter.json';
import QuoterV2ABI from './abi/UniswapV3QuoterV2.json';
import QuoterV4ABI from './abi/UniswapV4Quoter.json';
import V3FactoryABI from './abi/UniswapV3Factory.json';
import V3PoolABI from './abi/V3Pool.json';


// Internal modules
import { SupportedChains, type THexString, type TContractCommand, type ISwapParams , CHAIN_CONFIG, ROUTER_AS_RECIPIENT, type IPoolState, RouterCommands, type IQuoteResult, PERMIT2_ADDRESS, FeeAmount, getSupportedChain, type ISwapParamsNew, type TTokenInfo} from './types';
import { calculateBestSwapPath, getV3Quote, PoolType, type ISwapPath } from './path';
import { getTokenInfo } from './token';
import { asyncForEach } from '../utils';



/**
 * Adds a command to the Universal Router command array
 * @param universalSwapCommand - The command object to modify
 * @param command - The command code
 * @param parameters - The parameter types
 * @param values - The parameter values
 * @returns True if command was added successfully
 */
function addCommand(
  universalSwapCommand: TContractCommand,
  command: THexString,
  parameters: any[],
  values: any[]
): boolean {
  if (parameters.length === 0 || parameters.length !== values.length) {
    throw new Error('addCommand parameters mismatch');
  }
  
  const commandData = encodeAbiParameters(parameters, values);
  universalSwapCommand.command = (universalSwapCommand.command + command.substring(2)) as THexString;
  universalSwapCommand.inputs.push(commandData);
  return true;
}



/**
 * Unwraps WETH to ETH using Universal Router
 * @param amount - Amount of WETH to unwrap
 * @param recipient - Address to receive the ETH
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to transaction hash
 */
export async function unwrapETH(
  amount: number,
  recipient: THexString,
  walletClient: WalletClient
): Promise<string> {
  let chain = getSupportedChain(walletClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + walletClient.chain?.id
  }

  const config = CHAIN_CONFIG[chain];
  
  // Get account from private key
  const account = privateKeyToAccount((process.env.PRIVATE_KEY || '') as `0x${string}`);

  try {
    // Convert amount to wei
    const amountWei = BigInt(amount * 10 ** 18);

    // UNWRAP_ETH command type is 0x0c

    // Encode the command data
    const commandData = encodeAbiParameters(
      RouterCommands.UNWRAP_ETH.args,
      [recipient, amountWei]
    );

    // Execute the unwrap via Universal Router
    const txHash = await walletClient.writeContract({
      address: config.routerUniversalAddress as `0x${string}`,
      abi: universalRouterABI,
      functionName: 'execute',
      args: [
        RouterCommands.UNWRAP_ETH.command,
        [commandData],
        BigInt(Math.floor(Date.now() / 1000) + 60 * 10) // 10 minute deadline
      ],
      value: 0n, // No ETH sent
      account,
    } as any);

    return txHash;
  } catch (error) {
    console.error('Unwrap ETH failed:', error);
    throw error;
  }
}


/**
 * Gets the state of a Uniswap pool including price and liquidity
 * @param poolAddress - Address of the pool
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to pool state including sqrtPrice, liquidity and current tick
 */
export async function getPoolState(
  poolAddress: THexString,
  publicClient: PublicClient
): Promise<IPoolState> {
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  const config = CHAIN_CONFIG[chain];
  

  try {
    const slot0 = await publicClient.readContract({
      address: poolAddress,
      abi: V3PoolABI,
      functionName: 'slot0'
    }) as [bigint, number, number, number, number, number, boolean];

    const liquidity = await publicClient.readContract({
      address: poolAddress,
      abi: V3PoolABI,
      functionName: 'liquidity'
    }) as bigint;

    return {
      sqrtPriceX96: slot0[0],
      liquidity,
      tickCurrent: slot0[1]
    };
  } catch (error) {
    console.error('Error getting pool state:', error);
    throw error;
  }
}

/**
 * Gets the Uniswap V3 pool address for a token pair
 * @param tokenA - Address of first token
 * @param tokenB - Address of second token
 * @param fee - Fee tier (e.g. 3000 for 0.3%)
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to pool address
 */
export async function getUniswapV3Pool(
  tokenA: THexString,
  tokenB: THexString,
  fee: number,
  publicClient: PublicClient
): Promise<string> {
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  const config = CHAIN_CONFIG[chain];

  try {
    // Get pool address from factory contract
    const poolAddress: string = await publicClient.readContract({
      address: config.factoryAddress as `0x${string}`,
      abi: V3FactoryABI,
      functionName: 'getPool',
      args: [tokenA, tokenB, fee]
    }) as string;

    if (poolAddress === '0x0000000000000000000000000000000000000000') {
      throw new Error('Pool does not exist for this token pair and fee tier');
    }

    return poolAddress;
  } catch (error) {
    console.error('Error getting pool address:', error);
    throw error;
  }
}


export async function getUniswapQuoteStatic(
  tokenIn: TTokenInfo,
  tokenOut: TTokenInfo,
  amountIn: number,
  publicClient: PublicClient,
  path: ISwapPath

): Promise<IQuoteResult> {

  try {

  let amountInQuote = amountIn
  let amountOut = 0;


  await asyncForEach(path.hops, async (hop) => {
      let quote = await getV3Quote(
            hop.tokenIn,
            hop.tokenOut,
            amountInQuote,
            hop.fee,
            SupportedChains.BASE_MAINNET,
            hop.tokenIn.toLowerCase() == tokenIn.address.toLowerCase() ? tokenIn.decimals : 18,
            hop.tokenOut.toLowerCase() == tokenOut.address.toLowerCase() ? tokenOut.decimals : 18,
            publicClient
      )

      amountInQuote = quote?.amountOut || 0;
      amountOut = quote?.amountOut || 0;

  } )

    return {
      amountIn,
      amountOut: amountOut || 0,
      exchangeRate: amountOut / amountIn,
      tokenIn: tokenIn.address,
      tokenOut: tokenOut.address,
      gasEstimate: path.estimatedGas,
    };
  } catch (err: any) {
    console.log('Error fetching quote from uniswap:' + err.toString())
    return {
      amountIn,
      amountOut: path.effectiveOutput || 0,
      exchangeRate: path.totalOutputPerInput,
      tokenIn: tokenIn.address,
      tokenOut: tokenOut.address,
      gasEstimate: path.estimatedGas,
    };
  }
}

/**
 * Gets a quote for a swap on Uniswap - calcylates the best path then fetched the quotes for the path
 * @param tokenInAddress - Address of input token
 * @param tokenOutAddress - Address of output token
 * @param amountIn - Amount of input token
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to quote result including expected output amount and gas estimate
 */
export async function getUniswapQuote(
  tokenInAddress: THexString,
  tokenOutAddress: THexString,
  amountIn: number,
  publicClient: PublicClient

): Promise<IQuoteResult> {
  // Validate inputs
  if (!tokenInAddress || !tokenOutAddress) {
    throw new Error('Token addresses must be provided');
  }
  if (amountIn <= 0) {
    throw new Error('Amount must be greater than 0');
  }
  // Set up provider for Base Sepolia testnet
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  // get the token data for the unpit and output  
  let tokenIn = await getTokenInfo(publicClient, tokenInAddress)
  let tokenOut = await getTokenInfo(publicClient, tokenOutAddress)

  let path = await calculateBestSwapPath(
    tokenIn,
    tokenOut,
    amountIn,
    publicClient,
     {
      maxHops: 2, 
      includedPools: [PoolType.V3] // , PoolType.V4
    })


  if (!path || !path.effectiveOutput) {
    throw 'No valid path could be found'
  }


  return {
    amountIn,
    amountOut: path.effectiveOutput,
    exchangeRate: path.totalOutputPerInput,
    tokenIn: tokenIn.address,
    tokenOut: tokenOut.address,
    gasEstimate: path.estimatedGas,
  };

}



/**
 * Executes a token swap on Uniswap using the Universal Router - input is any token and output is any other token
 * @param params - Swap parameters including tokenIn, tokenOut, amountIn, recipient, slippageTolerance, and deadline
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to the amount of output tokens received
 */
export async function swapTokenForToken(
  paramsnew: ISwapParamsNew,
  publicClient: PublicClient,
  walletClient: WalletClient,
  path: ISwapPath
): Promise<{swapAmount: number, swapHash?: THexString}> {
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  const config = CHAIN_CONFIG[chain];

  // Get account from private key
  const walletAccount: any = walletClient.account 
  
  let account: THexString;

  if (walletAccount?.address) {
    account = walletAccount?.address
  } else {
    account = walletAccount
  }

  if (!account) {
    throw 'No account was found for the wallet client'
  }
  if (!paramsnew?.tokenIn?.eth_address) {
    throw 'No input token address was specified'
  }

  if (!paramsnew?.tokenOut?.eth_address) {
    throw 'No output token address was specified'
  }

  let tokenIn = paramsnew.tokenIn
  let tokenOut = paramsnew.tokenOut
  if (!tokenIn || !tokenIn.symbol) {
      throw 'Input token not found on chain'
  }
  if (!tokenOut || !tokenOut.symbol) {
    throw 'Output token not found on chain'
}


    if (!path) {
      throw 'No valid path could be found'
      return {swapAmount: 0}
    }


  try {

    // Initialise the universal router command
    let universalSwapCommand: TContractCommand = {
      command: '0x',
      inputs: []
    };
    
    
    // Check if tokens are already approved in the token contract for Permit2
    const currentAllowance = await publicClient.readContract({
      address: paramsnew.tokenIn.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [account, PERMIT2_ADDRESS]
    }) as bigint;
    
    const requiredAllowance = BigInt(Math.floor(paramsnew.amountIn * 10**tokenIn.decimals));
    
    // Only approve if the current allowance is insufficient
    if (currentAllowance < requiredAllowance) {
      console.log('Approving Permit2 contract to spend tokens...');
      
      const approvalHash = await walletClient.writeContract({
        address: paramsnew.tokenIn.address,
        abi: erc20Abi,
        functionName: 'approve',
        args: [
          PERMIT2_ADDRESS,
          BigInt(2**256) - BigInt(1) // Approve max amount
        ],
        account,
      } as any);

      // Wait for approval transaction to be confirmed
      console.log(`Approval transaction submitted: ${approvalHash}`);
      const approvalReceipt = await publicClient.waitForTransactionReceipt({ 
        hash: approvalHash 
      });
      console.log('Approval confirmed in block:', approvalReceipt.blockNumber);
    } else {
      console.log('Token already approved for Permit2, skipping approval');
    }
    
    // Step 2: Create the permit signature


    const deadline = BigInt(paramsnew.deadline);
    const amountInWei = BigInt(Math.floor(paramsnew.amountIn * 10**tokenIn.decimals));
          

    // Check current allowance in Permit2 contract
    const [permitAmount, permitExpiration, permitNonce] = await publicClient.readContract({
      address: PERMIT2_ADDRESS,
      abi: Permit2ABI,
      functionName: 'allowance',
      args: [account, paramsnew.tokenIn.address, config.routerUniversalAddress]
    }) as [bigint, bigint, bigint];
    
    if (permitAmount < amountInWei || permitExpiration < deadline) {

      
      // Permit2 domain data
      const domain = {
        name: 'Permit2',
        chainId: BigInt(config.id),
        verifyingContract: PERMIT2_ADDRESS as THexString
        //version: "1",
      };
      
      
      // Define the permit2 types
      const types = {
        PermitSingle: [
          { name: 'details', type: 'PermitDetails' },
          { name: 'spender', type: 'address' },
          { name: 'sigDeadline', type: 'uint256' }
        ],
        PermitDetails: [
          { name: 'token', type: 'address' },
          { name: 'amount', type: 'uint160' },
          { name: 'expiration', type: 'uint48' },
          { name: 'nonce', type: 'uint48' }
        ]
      };
      
      // Create the permit data
      const permitData = {
        details: {
          token: paramsnew.tokenIn.address,
          amount: BigInt(2**160) - BigInt(1), // max 160bit unsinged integer value
          expiration: deadline + (BigInt(60) * BigInt(60) * BigInt(24) * BigInt(30) ), // Set the permit expiration to 30 days in the fiture
          nonce: permitNonce
        },
        spender: config.routerUniversalAddress,
        sigDeadline: deadline
      };
      
      console.log('Signing permit2 message...');
      const signature = await walletClient.signTypedData({
        account,
        domain,
        types,
        primaryType: 'PermitSingle',
        message: permitData
      });
      
      // Add PERMIT2_PERMIT command
      addCommand(universalSwapCommand, RouterCommands.PERMIT2_PERMIT.command, RouterCommands.PERMIT2_PERMIT.args
        , [
        permitData,
        signature
      ]);
    }



    // Process each hop in the path
    let currentAmountIn = amountInWei;
    let v3PathSegment: {
      types: any[],
      parameters: any[],
      startTokenIn: THexString,
      endTokenOut: THexString
    } | null = null;
    
    for (let i = 0; i < path.hops.length; i++) {
      const hop = path.hops[i];
      const isLastHop = i === path.hops.length - 1;
      
      // Handle V3 hops by building path segments
      if (hop.poolType === PoolType.V3) {
        // Start a new V3 path segment if needed
        if (!v3PathSegment) {
          v3PathSegment = {
            types: ['address'],
            parameters: [hop.tokenIn],
            startTokenIn: hop.tokenIn,
            endTokenOut: hop.tokenOut
          };
        } else {
          // Update the end token of the current segment
          v3PathSegment.endTokenOut = hop.tokenOut;
        }
        
        // Add fee and tokenOut to the path
        v3PathSegment.types.push('uint24', 'address');
        v3PathSegment.parameters.push(hop.fee || FeeAmount.MEDIUM, hop.tokenOut);
        
        // If this is the last hop or the next hop is not V3, finalize this V3 segment
        if (isLastHop || path.hops[i + 1].poolType !== PoolType.V3) {
          const pathEncoded = encodePacked(v3PathSegment.types, v3PathSegment.parameters);
          
          addCommand(universalSwapCommand, RouterCommands.V3_SWAP_EXACT_IN.command,
            RouterCommands.V3_SWAP_EXACT_IN.args,
            [
              ROUTER_AS_RECIPIENT,
              currentAmountIn,
              0n, // amountOutMin (0 because we handle slippage elsewhere)
              pathEncoded,
              true // payerIsUser
            ]
          );
          
          // Reset for potential next segment
          v3PathSegment = null;
          
          // For multi-hop paths, we don't know the exact output amount
          // This is a simplification - in production you'd want to calculate this
          if (!isLastHop) {
            currentAmountIn = 0n; // This would need to be calculated from the quote
          }
        }
      }
      
      // Handle V4 hops
      else if (hop.poolType === PoolType.V4) {
        // If we have an open V3 segment, finalize it first
        if (v3PathSegment) {
          const pathEncoded = encodePacked(v3PathSegment.types, v3PathSegment.parameters);
          
          addCommand(universalSwapCommand, RouterCommands.V3_SWAP_EXACT_IN.command,
            RouterCommands.V3_SWAP_EXACT_IN.args,
            [
              ROUTER_AS_RECIPIENT,
              currentAmountIn,
              0n,
              pathEncoded,
              true
            ]
          );
          
          v3PathSegment = null;
          
          // For multi-hop paths, we don't know the exact output amount
          // This is a simplification - in production you'd want to calculate this
          if (!isLastHop) {
            currentAmountIn = 0n; // This would need to be calculated from the quote
          }
        }
        
        // Get token info to determine if tokenIn is token0 or token1
        const isToken0 = hop.tokenIn.toLowerCase() < hop.tokenOut.toLowerCase();
        
        // Create the pool key for V4
        const poolKey = {
          currency0: isToken0 ? hop.tokenIn : hop.tokenOut,
          currency1: isToken0 ? hop.tokenOut : hop.tokenIn,
          fee: hop.fee || FeeAmount.MEDIUM,
          tickSpacing: 60, // Medium fee tier typically has 60 tick spacing
          hooks: '0x0000000000000000000000000000000000000000' as `0x${string}`
        };
        
        // Add V4_SWAP command
        addCommand(universalSwapCommand, RouterCommands.V4_SWAP.command,
          RouterCommands.V4_SWAP.args,
          [
            ROUTER_AS_RECIPIENT, // recipient
            currentAmountIn, // amountIn
            0n, // amountOutMin (0 because we handle slippage elsewhere)
            poolKey, // poolKey
            !isToken0, // zeroForOne (true if swapping token0 for token1)
            '0x' as `0x${string}`, // hookData (empty for now)
            true // payerIsUser
          ]
        );
        
        // For multi-hop paths, we don't know the exact output amount
        // This is a simplification - in production you'd want to calculate this
        if (!isLastHop) {
          currentAmountIn = 0n; // This would need to be calculated from the quote
        }
      }
    }
    
    // transfer the result to the destination user
    addCommand(universalSwapCommand,RouterCommands.SWEEP.command,RouterCommands.SWEEP.args
      ,
         [
          paramsnew.tokenOut.address,
          paramsnew.recipient,
          100n,

         ]
    )

    // Debug logging only
    //  console.log('command', universalSwapCommand.command)
    //  console.log('command', universalSwapCommand.inputs)
    //  if (1==1) return 0
     
    // Execute the transaction
    const swapHash = await walletClient.writeContract({
      address: config.routerUniversalAddress as `0x${string}`,
      abi: universalRouterABI,
      functionName: 'execute',
      gas: 2000000n,
      args: [
        universalSwapCommand.command,
        universalSwapCommand.inputs,
        BigInt(paramsnew.deadline)
      ],
      value: 0n,
      account,
    } as any);

    console.log(`Swap transaction submitted: ${swapHash}`);
    const swapReceipt = await publicClient.waitForTransactionReceipt({ 
      hash: swapHash as THexString
    });

    
    if (swapReceipt.status !== 'success') {
      return {swapAmount: 0}
    }
    console.log('Swap confirmed in block:', swapReceipt.status, swapReceipt.blockNumber);
    
    let swapAmount = await getSwapOutputAmount(swapHash,
      paramsnew.tokenOut.address,
      paramsnew.recipient,
      publicClient)


    return {swapAmount, swapHash}

  } catch (error) {
    console.error('V4 Swap failed:', error);
    throw error;
  }
}


/**
 * Executes a token swap on Uniswap using the Universal Router - input is any token and output is ETH
 * @param params - Swap parameters including tokenIn, amountIn, recipient, slippageTolerance, and deadline
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to the amount of ETH received
 */
export async function swapTokenForEth(
  paramsnew: ISwapParamsNew,
  publicClient: PublicClient,
  walletClient: WalletClient
): Promise<{swapAmount: number, swapHash?: THexString}> {
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  // Get account from private key
  const walletAccount: any = walletClient.account 
  
  let account: THexString;

  if (walletAccount?.address) {
    account = walletAccount?.address
  } else {
    account = walletAccount
  }

  const config = CHAIN_CONFIG[chain];

  // Get account from private key
  if (!paramsnew.tokenIn?.address) {
    throw 'No token address was specified'
    return {swapAmount: 0}
  }

  if (paramsnew.tokenIn.address.toLowerCase() === config.weth_address.toLowerCase()) {
    throw 'The source for eth token swap cant be weth'
    return {swapAmount: 0}
  }

  

  try {

    // Initialise the universal router command
    let universalSwapCommand: TContractCommand = {
      command: '0x',
      inputs: []
    };
    
    
    // Check if tokens are already approved in the token contract for Permit2
    const currentAllowance = await publicClient.readContract({
      address: paramsnew.tokenIn.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [account, PERMIT2_ADDRESS]
    }) as bigint;
    
    const requiredAllowance = BigInt(Math.floor(paramsnew.amountIn * 10**paramsnew.tokenIn.decimals));
    
    // Only approve if the current allowance is insufficient
    if (currentAllowance < requiredAllowance) {
      console.log('Approving Permit2 contract to spend tokens...');
      
      const approvalHash = await walletClient.writeContract({
        address: paramsnew.tokenIn.address,
        abi: erc20Abi,
        functionName: 'approve',
        args: [
          PERMIT2_ADDRESS,
          BigInt(2**256) - BigInt(1) // Approve max amount
        ],
        account,
      } as any);

      // Wait for approval transaction to be confirmed
      console.log(`Approval transaction submitted: ${approvalHash}`);
      const approvalReceipt = await publicClient.waitForTransactionReceipt({ 
        hash: approvalHash 
      });
      console.log('Approval confirmed in block:', approvalReceipt.blockNumber);
    } else {
      console.log('Token already approved for Permit2, skipping approval');
    }
    
    // Step 2: Create the permit signature


    const deadline = BigInt(paramsnew.deadline);
    const amountInWei = BigInt(Math.floor(paramsnew.amountIn * 10**paramsnew.tokenIn.decimals));
          

    // Check current allowance in Permit2 contract
    const [permitAmount, permitExpiration, permitNonce] = await publicClient.readContract({
      address: PERMIT2_ADDRESS,
      abi: Permit2ABI,
      functionName: 'allowance',
      args: [account, paramsnew.tokenIn.address, config.routerUniversalAddress]
    }) as [bigint, bigint, bigint];
    
    if (permitAmount < amountInWei || permitExpiration < deadline) {

      
      // Permit2 domain data
      const domain = {
        name: 'Permit2',
        chainId: BigInt(config.id),
        verifyingContract: PERMIT2_ADDRESS as THexString
        //version: "1",
      };
      
      
      // Define the permit2 types
      const types = {
        PermitSingle: [
          { name: 'details', type: 'PermitDetails' },
          { name: 'spender', type: 'address' },
          { name: 'sigDeadline', type: 'uint256' }
        ],
        PermitDetails: [
          { name: 'token', type: 'address' },
          { name: 'amount', type: 'uint160' },
          { name: 'expiration', type: 'uint48' },
          { name: 'nonce', type: 'uint48' }
        ]
      };
      
      // Create the permit data
      const permitData = {
        details: {
          token: paramsnew.tokenIn.address,
          amount: BigInt(2**160) - BigInt(1), // max 160bit unsinged integer value
          expiration: deadline + (BigInt(60) * BigInt(60) * BigInt(24) * BigInt(30) ), // Set the permit expiration to 30 days in the fiture
          nonce: permitNonce
        },
        spender: config.routerUniversalAddress,
        sigDeadline: deadline
      };
      
      console.log('Signing permit2 message...');
      const signature = await walletClient.signTypedData({
        account,
        domain,
        types,
        primaryType: 'PermitSingle',
        message: permitData
      });
      
      // Add PERMIT2_PERMIT command
      addCommand(universalSwapCommand, RouterCommands.PERMIT2_PERMIT.command, RouterCommands.PERMIT2_PERMIT.args
        , [
        permitData,
        signature
      ]);
    }
    
    // Create the path for the swap
    const path = encodePacked(
      ['address', 'uint24', 'address'],
      [paramsnew.tokenIn.address, FeeAmount.MEDIUM, config.weth_address]
    );
    
    // Add V3_SWAP_EXACT_IN command
    addCommand(universalSwapCommand, RouterCommands.V3_SWAP_EXACT_IN.command,
      RouterCommands.V3_SWAP_EXACT_IN.args,
      [
        ROUTER_AS_RECIPIENT, //account.address
        amountInWei,
        0n,
        path,
        true
      ]
    );
    
    // Add UNWRAP_ETH command to unwrap the ETH received from the swap
    addCommand(universalSwapCommand, RouterCommands.UNWRAP_ETH.command, RouterCommands.UNWRAP_ETH.args, [paramsnew.recipient, 100n]);

    // Execute the transaction
    const swapHash = await walletClient.writeContract({
      address: config.routerUniversalAddress as `0x${string}`,
      abi: universalRouterABI,
      functionName: 'execute',
      gas: 1000000n,
      args: [
        universalSwapCommand.command,
        universalSwapCommand.inputs,
        BigInt(paramsnew.deadline)
      ],
      value: 0n,
      account,
    } as any);

    console.log(`Swap transaction submitted: ${swapHash}`);
    const swapReceipt = await publicClient.waitForTransactionReceipt({ 
      hash: swapHash as THexString
    });
    console.log('Swap confirmed in block:', swapReceipt.status, swapReceipt.blockNumber);
    
    let swapAmount = await getSwapOutputAmount(swapHash,
      config.weth_address,
      config.routerUniversalAddress,
      publicClient)


    return {swapAmount, swapHash};
  } catch (error) {
    console.error('V4 Swap failed:', error);
    throw error;
  }
}

/**
 * Executes a token swap on Uniswap using the Universal Router - input is ETH and output is any token
 * @param params - Swap parameters including tokenOut, amountIn, recipient, slippageTolerance, and deadline
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to the amount of tokens received
 */
export async function swapEthForToken(
  paramsnew: ISwapParamsNew,
  publicClient: PublicClient,
  walletClient: WalletClient
): Promise<{swapAmount: number, swapHash?: THexString}> {

  
  let chain = getSupportedChain(publicClient.chain?.id || 0 )

  
  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  } 

  const config = CHAIN_CONFIG[chain] ;

  // Get account from private key
  const account = walletClient.account;


  if (!paramsnew.tokenOut?.address) {
    throw 'No token out address was specified'
    return { swapAmount: 0}
  }
  if (paramsnew.tokenOut.address.toLowerCase() === config.weth_address.toLowerCase()) {
    throw 'The destination for eth to token swap cant be weth'
    return { swapAmount: 0}
  }

  try {

    
    // Encode the command data

   
    let universalSwapCommand: TContractCommand = {
      command: '0x',
      inputs: []
    }
    
    // Create the commands and inputs arrays


    addCommand(universalSwapCommand, RouterCommands.WRAP_ETH.command,RouterCommands.WRAP_ETH.args
      , [ROUTER_AS_RECIPIENT, BigInt(Math.floor(paramsnew.amountIn * 10 ** 18))])

    const path = encodePacked(
      ['address', 'uint24' ,  'address' ],
      [config.weth_address, FeeAmount.MEDIUM, paramsnew.tokenOut.address]
    );

    addCommand(universalSwapCommand,RouterCommands.V3_SWAP_EXACT_IN.command,
      RouterCommands.V3_SWAP_EXACT_IN.args,
         [
          ROUTER_AS_RECIPIENT,
          BigInt(Math.floor(paramsnew.amountIn * 10 ** 18)),
          0n,
        path,
        false
         ]
    )


    addCommand(universalSwapCommand,RouterCommands.SWEEP.command,RouterCommands.SWEEP.args
      ,
         [
          paramsnew.tokenOut.address,
          paramsnew.recipient,
          100n,

         ]
    )
    // console.log('universalSwapCommand.inputs', universalSwapCommand.inputs)
    // console.log('universalSwapCommand.command', universalSwapCommand.command)



    const  result = await publicClient.simulateContract({
            address: config.routerUniversalAddress as `0x${string}`,
      abi: universalRouterABI,
      functionName: 'execute',
      //gas: 1000000n,
      args: [
        universalSwapCommand.command,
        universalSwapCommand.inputs,
        BigInt(paramsnew.deadline)
      ],
      value: BigInt(Math.floor(paramsnew.amountIn * 10 ** 18)), 
      account,
    })
    
    
    let swapHash2:THexString = await walletClient.writeContract({
      address: config.routerUniversalAddress as `0x${string}`,
      abi: universalRouterABI,
      functionName: 'execute',
      gas: 1000000n,
      args: [
        universalSwapCommand.command,
        universalSwapCommand.inputs,
        BigInt(paramsnew.deadline)
      ],
      value: BigInt(Math.floor(paramsnew.amountIn * 10 ** 18)), 
      account,
    } as any);

    //console.log(`Swap transaction submitted: ${swapHash2}`);
    const approvalReceipt = await publicClient.waitForTransactionReceipt({ 
      hash: swapHash2 as THexString
    });
    //console.log('Swap confirmed in block:', approvalReceipt.status, approvalReceipt.blockNumber);

    let swapAmount = await getSwapOutputAmount(swapHash2,
            paramsnew.tokenOut.address,
            paramsnew.recipient,
            publicClient)
    
    
    return {swapAmount, swapHash: swapHash2};


  } catch (error) {
    console.error('V4 Swap failed:', error);
    throw error;
  }
}

/**
 * Retrieves the actual token output amount from a completed swap transaction
 * @param txHash - Transaction hash of the completed swap
 * @param tokenAddress - Address of the output token to track
 * @param recipientAddress - Address of the recipient who received the tokens
 * @param chain - Chain to use (default: Base Sepolia)
 * @returns Promise resolving to the amount of tokens received
 */
export async function getSwapOutputAmount(
  txHash: THexString,
  tokenAddress: THexString,
  recipientAddress: THexString,
  publicClient: PublicClient,

): Promise<number> {
  let chain = getSupportedChain(publicClient.chain?.id || 0)

  if (!chain) {
    throw 'Chain id is unsupported for uniswap ' + publicClient.chain?.id
  }

  const config = CHAIN_CONFIG[chain];
  
  
  try {
    // Get transaction receipt
    const receipt = await publicClient.getTransactionReceipt({
      hash: txHash
    });
    
    if (!receipt || receipt.status !== 'success') {
      throw new Error(`Transaction ${txHash} failed or not found`);
    }
  
    // Get token decimals
    const decimals = await publicClient.readContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'decimals'
    }) as number;

    // Look for Transfer events to the recipient
    let totalAmount = 0n;
    
    // Parse logs for ERC20 Transfer events
    // Transfer event signature: Transfer(address indexed from, address indexed to, uint256 value)
    const transferEventSignature = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
    
    for (const log of receipt.logs) {
      // Check if this log is from the token contract we're interested in
      if (log.address.toLowerCase() === tokenAddress.toLowerCase() && 
          log.topics[0] === transferEventSignature) {
        
        // Check if recipient is the destination (topic[2] is the 'to' address)
        const toAddress = `0x${log.topics[2]?.substring(26)}` as THexString;
        
        if (toAddress.toLowerCase() === recipientAddress.toLowerCase()) {
          // Parse the amount from the data field
          const amount = BigInt(log.data);
          totalAmount += amount;
        }
      }
    }
    
    // Convert from wei to token units based on decimals
    return Number(totalAmount) / (10 ** decimals);
  } catch (error) {
    console.error('Error retrieving swap output amount:', error);
    throw error;
  }
}
